import { getAccessToken, saveLocalStorage } from 'configs/localStorage';
import { transformErrorResponse } from 'store/redux-toolkit-common';

import GA from 'constants/ga';
import ReactGA from 'react-ga';

import { isAuth } from 'store/slices/authorizations';
import { changeUserData } from 'store/slices/profile';

import { message } from 'style-guide/ToastMessages';

import emptySplitApi from '..';

const userApi = emptySplitApi.injectEndpoints({
  reducerPath: 'user',
  endpoints: (build) => ({
    createUser: build.mutation({
      query: (params) => ({
        url: '/users',
        method: 'POST',
        body: params,
      }),
    }),

    sendResetPassword: build.mutation({
      query: (params) => ({
        url: '/authmanagement',
        method: 'POST',
        body: { action: 'sendResetPwd', value: { email: params.email } },
      }),

      transformResponse: (_response, _meta, params) => {
        ReactGA.event({
          category: GA.categories.users,
          action: GA.actions.forgotPassword,
          label: params?.domainKey,
        });
      },
    }),

    verifyEmailApi: build.mutation({
      query: (params) => ({
        url: '/authmanagement',
        method: 'POST',
        body: {
          action: 'verifySignupLong',
          value: params.token,
        },
      }),
      transformErrorResponse: () => {
        message.error(
          'To verify your email address, please follow this single-use link: Finish your registration and Sign In'
        );
      },
    }),

    resetPassword: build.mutation({
      query: (params) => ({
        url: '/authmanagement',
        method: 'POST',
        body: {
          action: 'resetPwdLong',
          value: {
            token: params.token,
            password: params.password,
          },
        },
      }),
    }),

    changePassword: build.mutation({
      query: (params) => ({
        url: '/authmanagement',
        method: 'POST',
        body: {
          action: 'passwordChange',
          value: {
            oldPassword: params.oldPassword,
            password: params.newPassword,
            user: { email: params.email },
          },
        },
      }),
    }),

    authenticationUser: build.mutation({
      query: (params) => ({
        url: '/authentication',
        method: 'POST',
        body: { ...params, strategy: 'local' },
      }),
      invalidatesTags: ['getPopularCourses'],
      transformErrorResponse,
      transformResponse: (response, _meta, params) => {
        window.pendo.initialize({
          visitor: {
            id: response.id,
          },
          account: {
            id: response.domainId,
          },
        });
        if (response.adminUser === true) {
          saveLocalStorage({
            adminUser: response,
          });
          return {
            adminUser: response,
          };
        }
        ReactGA.event({
          category: GA.categories.users,
          action: GA.actions.signIn,
          label: params?.domainKey,
        });
        saveLocalStorage({
          user: response,
        });
        return {
          user: response,
        };
      },

      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        const { data } = await queryFulfilled;
        if (data.user?.accessToken || data.adminUser?.accessToken) {
          dispatch(isAuth({ isAuth: true, userData: data }));
          dispatch(changeUserData({ isAuth: true, userData: data.user }));
        }
      },
    }),

    logOut: build.mutation({
      query: () => ({
        url: '/authentication',
        method: 'DELETE',
        body: { accessToken: getAccessToken() },
      }),
    }),
  }),

  overrideExisting: false,
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints

export const {
  useCreateUserMutation,
  useAuthenticationUserMutation,
  useSendResetPasswordMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useLogOutMutation,
  useVerifyEmailApiMutation,
} = userApi;
export default userApi;
